export type DiceTerm = {
	type: 'dice';
	count: number;
	sides: number;
	sign: 1 | -1;
	/** в сумму идут только лучшие или худшие кости: 2d20kh1 — преимущество, 2d20kl1 — помеха */
	keep?: {highest: boolean; count: number};
};

export type NumberTerm = {
	type: 'number';
	value: number;
	sign: 1 | -1;
};

export type FormulaTerm = DiceTerm | NumberTerm;

export type DiceGroupResult = {
	term: DiceTerm;
	values: number[];
	subtotal: number;
};

export type FormulaResult = {
	groups: DiceGroupResult[];
	modifier: number;
	total: number;
};

function normalize(s: string): string {
	return s
		// non-breaking, thin, narrow, zero-width spaces, BOM, ideographic space
		.replace(/[   ⁠﻿　]+/g, ' ')
		// unicode minus, en-dash, em-dash -> ASCII minus
		.replace(/[−–—]/g, '-')
		.replace(/\s+/g, ' ')
		.trim();
}

export function parseFormula(input: string): FormulaTerm[] {
	const s = normalize(input);
	const withSign = /^[+-]/.test(s) ? s : `+${s}`;
	const terms: FormulaTerm[] = [];
	const re = /([+-])\s*(\d+)?\s*[dD](\d+)(?:k([hl])(\d+))?|([+-])\s*(\d+)/g;
	let m: RegExpExecArray | null;
	while ((m = re.exec(withSign))) {
		if (m[3] !== undefined) {
			const count = parseInt(m[2] || '1', 10);
			const sides = parseInt(m[3], 10);
			const keepCount = m[5] !== undefined ? parseInt(m[5], 10) : 0;
			if (count > 0 && sides > 0) {
				terms.push({
					type: 'dice',
					sign: m[1] === '-' ? -1 : 1,
					count,
					sides,
					...(keepCount > 0 && keepCount < count ? {keep: {highest: m[4] === 'h', count: keepCount}} : null),
				});
			}
		} else if (m[7] !== undefined) {
			terms.push({type: 'number', sign: m[6] === '-' ? -1 : 1, value: parseInt(m[7], 10)});
		}
	}
	return terms;
}

export function keptIndexes(term: DiceTerm, values: number[]): Set<number> {
	if (!term.keep) return new Set(values.map((_, index) => index));
	const order = values
		.map((value, index) => ({value, index}))
		.sort((first, second) => (term.keep!.highest ? second.value - first.value : first.value - second.value));
	return new Set(order.slice(0, term.keep.count).map(({index}) => index));
}

export function termSubtotal(term: DiceTerm, values: number[]): number {
	const kept = keptIndexes(term, values);
	return values.reduce((sum, value, index) => sum + (kept.has(index) ? value : 0), 0) * term.sign;
}

function rollOne(sides: number): number {
	return Math.floor(Math.random() * sides) + 1;
}

export function rollFormula(terms: FormulaTerm[]): FormulaResult {
	const groups: DiceGroupResult[] = [];
	let modifier = 0;

	for (const term of terms) {
		if (term.type === 'dice') {
			const values = Array.from({length: term.count}, () => rollOne(term.sides));
			groups.push({term, values, subtotal: termSubtotal(term, values)});
		} else {
			modifier += term.value * term.sign;
		}
	}

	const total = groups.reduce((a, g) => a + g.subtotal, 0) + modifier;
	return {groups, modifier, total};
}
